import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateTransaction } from "@factory/query/transaction.query";
import {
  MemberFormSchema,
  TransactionFormRule,
  TransactionFormSchema,
} from "@factory/form/form.rule";
import { transactionFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { format } from "date-fns";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useQyCreateMember } from "@factory/query/member.query";
import { GetMemberModel } from "@factory/model/query-model-member";
import { MessageResponseDto } from "@api/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTransactionStepContext } from "./transaction-step.context";
import { LabelValue } from "@shared/models/label-value.interface";
import { useReactToPrint } from "react-to-print";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import {
  CreateTransactionWaiverModel,
  CreateTransactionWaiverModelExtension,
} from "@factory/model/query-model-transaction-waiver";
import { MutateResponseTransactionModel } from "@factory/model/query-model-transaction";
import { getBirthdateFromAge } from "@shared/utility/date-time-service/birthday";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useQyCreateTransactionWaiver } from "@factory/query/transaction-waiver.query";

export function useNewTransaction() {
  // LOCAL DECLARATION
  const {
    isParentAndChild,
    isCreateNewChild,
    selectedParent,
    selectedMember,
    parentFormData,
    memberFormData,
    childFormData,
    serviceFormData,
    addonFormData,
    selectedWaiverForm,
    parentList,
    memberList,

    setChildrenList,
    setSelectedParent,
    setSelectedMember,
    setParentFormData,
    setMemberFormData,

    previousStepper,
    getActiveMemberEndDateTime,
    clearLocalStorage,

    // Persisted states from step context
    isCreateNewParent,
    isCreateNewMember,
    pendingParentForm,
    setIsCreateNewParent,
    setIsCreateNewMember,
    setPendingParentForm,
  } = useTransactionStepContext();

  const { getDateTimeLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const { mapCreateTransaction, mapCreateMember } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  const [addNewChild, setAddNewChild] = useState(false); // useState(childrenList.length === 0);
  const [isCreating, setIsCreating] = useState(false);
  const [parentInfo, setParentInfo] = useState<GetMemberModel | null>(null);
  const [memberInfo, setMemberInfo] = useState<GetMemberModel | null>(null);

  // Sync parentInfo with selectedParent when parentList changes or on load
  useEffect(() => {
    if (selectedParent && parentList && parentList.length > 0) {
      const foundParent = parentList.find((item) => item.value === selectedParent);
      if (foundParent && foundParent.payload) {
        const payload = foundParent.payload;
        if (!parentInfo || parentInfo.code !== payload.code) {
          setParentInfo(payload);
          
          if (payload.avatar) {
            viewAsset(payload.avatar)
              .then((elementFile) => {
                const fileBlob = URL.createObjectURL(elementFile);
                setParentInfo((prev) => prev && prev.code === payload.code ? ({ ...prev, fileBlob } as GetMemberModel) : prev);
              })
              .catch((err) => console.error("Failed to load parent avatar", err));
          }
        }
      }
    } else if (!selectedParent && parentInfo !== null) {
      setParentInfo(null);
    }
  }, [selectedParent, parentList, parentInfo]);

  // Sync memberInfo with selectedMember when memberList changes or on load
  useEffect(() => {
    if (selectedMember && memberList && memberList.length > 0) {
      const foundMember = memberList.find((item) => item.value === selectedMember);
      if (foundMember && foundMember.payload) {
        const payload = foundMember.payload;
        if (!memberInfo || memberInfo.code !== payload.code) {
          setMemberInfo(payload);
          
          if (payload.avatar) {
            viewAsset(payload.avatar)
              .then((elementFile) => {
                const fileBlob = URL.createObjectURL(elementFile);
                setMemberInfo((prev) => prev && prev.code === payload.code ? ({ ...prev, fileBlob } as GetMemberModel) : prev);
              })
              .catch((err) => console.error("Failed to load member avatar", err));
          }
        }
      }
    } else if (!selectedMember && memberInfo !== null) {
      setMemberInfo(null);
    }
  }, [selectedMember, memberList, memberInfo]);

  const componentRef = useRef(null);

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    setIsCreating(false);
    clearLocalStorage();
    showSuccess(`New Transaction is created`);
    navigate(`../${data.code}`);
  };
  const { mutateAsync: addTransaction } =
    useQyCreateTransaction(handleApiSuccess);
  const { mutateAsync: addChild } = useQyCreateMember();
  const { mutateAsync: addParent } = useQyCreateMember();
  const { mutateAsync: addMember } = useQyCreateMember();
  const { mutateAsync: newWaiver } = useQyCreateTransactionWaiver();

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransactionFormSchema>({
    defaultValues: transactionFormDefault,
    resolver: zodResolver(TransactionFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: TransactionFormSchema) => {
    const formData = mapCreateTransaction(form);
    addTransaction(formData);
  };
  const handleValidateError = (err: FieldErrors<TransactionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const createChildren = useCallback(async () => {
    setIsCreating(true);
    const childResponses = await Promise.all(
      childFormData.map((child) => {
        let birthday = child.birthday;
        if (child.age && !child.birthday) {
          birthday = getBirthdateFromAge(child.age).toString();
        }
        const updatedForm = {
          ...child,
          isChild: true,
          parentCode: selectedParent,
          birthday,
        } as MemberFormSchema;
        const childForm = mapCreateMember(updatedForm);
        return addChild(childForm);
      })
    );
    const childMember = childResponses.map((child) => {
      const childData = child.data as GetMemberModel;
      return new LabelValue(
        `${childData.first_name} ${childData.last_name}`,
        childData.code || ""
      );
    });
    setChildrenList((children) => [...children, ...childMember]);

    setIsCreating(false);
    return childMember;
  }, [childFormData, selectedParent, mapCreateMember, addChild, setChildrenList]);

  const createChildrenByParentCode = useCallback(
    async (parentCode: string) => {
      if (!parentCode) {
        return [];
      }

      setIsCreating(true);
      const childResponses = await Promise.all(
        childFormData.map((child) => {
          let birthday = child.birthday;
          if (child.age && !child.birthday) {
            birthday = getBirthdateFromAge(child.age).toString();
          }
          const updatedForm = {
            ...child,
            isChild: true,
            parentCode,
            birthday,
          } as MemberFormSchema;
          const childForm = mapCreateMember(updatedForm);
          return addChild(childForm);
        })
      );
      const childMember = childResponses.map((child) => {
        const childData = child.data as GetMemberModel;
        return new LabelValue(
          `${childData.first_name} ${childData.last_name}`,
          childData.code || ""
        );
      });
      setChildrenList((children) => [...children, ...childMember]);

      setIsCreating(false);
      return childMember;
    },
    [childFormData, mapCreateMember, addChild, setChildrenList]
  );

  const createChildrenByParentCodeAndForms = useCallback(
    async (parentCode: string, forms: MemberFormSchema[]) => {
      if (!parentCode || forms.length === 0) {
        return [];
      }

      setIsCreating(true);
      const childResponses = await Promise.all(
        forms.map((child) => {
          let birthday = child.birthday;
          if (child.age && !child.birthday) {
            birthday = getBirthdateFromAge(child.age).toString();
          }
          const updatedForm = {
            ...child,
            isChild: true,
            parentCode,
            birthday,
          } as MemberFormSchema;
          const childForm = mapCreateMember(updatedForm);
          return addChild(childForm);
        })
      );
      const childMember = childResponses.map((child) => {
        const childData = child.data as GetMemberModel;
        return new LabelValue(
          `${childData.first_name} ${childData.last_name}`,
          childData.code || ""
        );
      });
      setChildrenList((children) => [...children, ...childMember]);

      setIsCreating(false);
      return childMember;
    },
    [mapCreateMember, addChild, setChildrenList]
  );

  const createParent = useCallback(async () => {
    setIsCreating(true);

    let birthday = parentFormData?.birthday;
    if (parentFormData?.age && !parentFormData.birthday) {
      birthday = getBirthdateFromAge(parentFormData.age).toString();
    }

    const parentPayload = mapCreateMember({
      ...parentFormData,
      birthday,
      isGuardian: true,
    } as MemberFormSchema);
    const parent = await addParent(parentPayload);
    const parentData = parent.data as GetMemberModel;

    let fileBlob = undefined;
    if (parentData.avatar) {
      const elementFile = await viewAsset(parentData.avatar);
      fileBlob = URL.createObjectURL(elementFile);
    }

    setParentInfo({ ...parentData, fileBlob });
    setSelectedParent(parentData.code || "");
    setIsCreating(false);
    setParentFormData(null);
  }, [parentFormData, mapCreateMember, addParent, setSelectedParent, setParentFormData]);

  const createParentAndGetCode = useCallback(async () => {
    setIsCreating(true);

    let birthday = parentFormData?.birthday;
    if (parentFormData?.age && !parentFormData.birthday) {
      birthday = getBirthdateFromAge(parentFormData.age).toString();
    }

    const parentPayload = mapCreateMember({
      ...parentFormData,
      birthday,
      isGuardian: true,
    } as MemberFormSchema);
    const parent = await addParent(parentPayload);
    const parentData = parent.data as GetMemberModel;

    let fileBlob = undefined;
    if (parentData.avatar) {
      const elementFile = await viewAsset(parentData.avatar);
      fileBlob = URL.createObjectURL(elementFile);
    }

    setParentInfo({ ...parentData, fileBlob });
    setSelectedParent(parentData.code || "");
    setIsCreating(false);
    setParentFormData(null);
    return parentData.code || "";
  }, [parentFormData, mapCreateMember, addParent, setSelectedParent, setParentFormData]);

  const createParentAndGetCodeFromForm = useCallback(
    async (formData: MemberFormSchema) => {
      setIsCreating(true);

      let birthday = formData?.birthday;
      if (formData?.age && !formData.birthday) {
        birthday = getBirthdateFromAge(formData.age).toString();
      }

      const parentPayload = mapCreateMember({
        ...formData,
        birthday,
        isGuardian: true,
      } as MemberFormSchema);
      const parent = await addParent(parentPayload);
      const parentData = parent.data as GetMemberModel;

      let fileBlob = undefined;
      if (parentData.avatar) {
        const elementFile = await viewAsset(parentData.avatar);
        fileBlob = URL.createObjectURL(elementFile);
      }

      setParentInfo({ ...parentData, fileBlob });
      setSelectedParent(parentData.code || "");
      setIsCreating(false);
      return parentData.code || "";
    },
    [mapCreateMember, addParent, setSelectedParent]
  );

  const createParentAndGuardianAndGetCode = useCallback(
    async (
      parentForm: MemberFormSchema,
      guardianForm: MemberFormSchema | null,
      addAsGuardian: boolean
    ) => {
      setIsCreating(true);
      try {
        const parentPayload = mapCreateMember({
          ...parentForm,
          isGuardian: addAsGuardian,
        } as MemberFormSchema);
        const parent = await addParent(parentPayload);
        const parentData = parent.data as GetMemberModel;
        const parentCode = parentData.code || "";

        let activeGuardianCode = parentCode;

        if (!addAsGuardian && guardianForm) {
          const guardianPayload = mapCreateMember({
            ...guardianForm,
            isGuardian: true,
          } as MemberFormSchema);
          const guardian = await addParent(guardianPayload);
          const guardianData = guardian.data as GetMemberModel;
          activeGuardianCode = guardianData.code || "";
        }

        let fileBlob = undefined;
        if (parentData.avatar) {
          try {
            const elementFile = await viewAsset(parentData.avatar);
            fileBlob = URL.createObjectURL(elementFile);
          } catch (e) {
            console.error("Failed to load avatar", e);
          }
        }

        setParentInfo({ ...parentData, fileBlob });
        setSelectedParent(activeGuardianCode);
        return { parentCode, guardianCode: activeGuardianCode };
      } finally {
        setIsCreating(false);
      }
    },
    [mapCreateMember, addParent, setSelectedParent, setParentInfo]
  );

  /**
   * Creates the pending parent/guardian in the DB (if a new one was entered in step 2)
   * and returns the guardian code to use for the transaction.
   * Returns the already-selected existing parent code if no pending form exists.
   */
  const createParentAndGuardianFromPending = useCallback(async (): Promise<string> => {
    if (!pendingParentForm) {
      // Existing guardian was selected — nothing to create
      return selectedParent || "";
    }

    const { parentCode, guardianCode } = await createParentAndGuardianAndGetCode(
      pendingParentForm.parentForm,
      pendingParentForm.guardianForm,
      pendingParentForm.addAsGuardian
    );

    // If it was a new-child flow we also need to create the children under the parent
    if (isCreateNewChild) {
      await createChildrenByParentCode(parentCode);
    }

    setPendingParentForm(null);
    return guardianCode;
  }, [
    pendingParentForm,
    selectedParent,
    isCreateNewChild,
    createParentAndGuardianAndGetCode,
    createChildrenByParentCode,
  ]);

  // NEW: Function for creating individual members
  const createMember = useCallback(async () => {
    setIsCreating(true);
    let birthday = memberFormData?.birthday;
    if (memberFormData?.age && !memberFormData.birthday) {
      birthday = getBirthdateFromAge(memberFormData.age).toString();
    }

    const memberPayload = mapCreateMember({
      ...memberFormData,
      birthday,
      is_child: false,
    } as MemberFormSchema);
    const member = await addMember(memberPayload);
    const memberData = member.data as GetMemberModel;

    let fileBlob = undefined;
    if (memberData.avatar) {
      const elementFile = await viewAsset(memberData.avatar);
      fileBlob = URL.createObjectURL(elementFile);
    }

    setMemberInfo({ ...memberData, fileBlob });
    setSelectedMember(memberData.code || "");
    setIsCreating(false);
    setMemberFormData(null);
  }, [
    memberFormData,
    mapCreateMember,
    addMember,
    setMemberInfo,
    setSelectedMember,
    setMemberFormData,
  ]);

  const createTransactionFromServices = useCallback(
    async (isPaid: boolean) => {
      if (!selectedWaiverForm) {
        showWarning("Missing waiver. Please select one");
        previousStepper();
        return;
      }

      setIsCreating(true);

      let guardianCode = selectedParent || undefined;
      let parentCode = selectedParent || "";
      let createdChildren: LabelValue[] = [];

      try {
        // 1. Create guardian in DB if it's a new guardian
        if (pendingParentForm) {
          const result = await createParentAndGuardianAndGetCode(
            pendingParentForm.parentForm,
            pendingParentForm.guardianForm,
            pendingParentForm.addAsGuardian
          );
          parentCode = result.parentCode;
          guardianCode = result.guardianCode;
          setPendingParentForm(null);
        }

        // 2. Create children in DB if it's a new child flow
        if (isCreateNewChild) {
          createdChildren = await createChildrenByParentCode(parentCode);
        }

        // 3. Map temporary 'new-index' member values to real child codes
        const serviceWithGuardian = serviceFormData.map((item) => {
          let memberCode = item.member;
          if (isCreateNewChild && memberCode && memberCode.startsWith("new-")) {
            const index = parseInt(memberCode.replace("new-", ""), 10);
            if (!isNaN(index) && createdChildren[index]) {
              memberCode = createdChildren[index].value;
            }
          }

          let date = item.date;
          let time = item.time;
          if (!date && !time && memberCode) {
            const activeEnd = getActiveMemberEndDateTime(memberCode);
            if (activeEnd) {
              const manilaActiveEnd = getDateTimeLocal(activeEnd, "Asia/Manila");
              date = format(manilaActiveEnd, "yyyy-MM-dd");
              time = format(manilaActiveEnd, "HH:mm");
            }
          }

          return {
            ...item,
            member: memberCode,
            guardian: guardianCode || undefined,
            date,
            time,
          };
        });

        const form = mapCreateTransaction({
          service: serviceWithGuardian,
          addon: addonFormData,
          isPaid,
        });

        const response: MutateResponseTransactionModel = await addTransaction(
          form
        );
        const dataResponse = response.data;

        try {
          const updatedWaiver = {
            ...selectedWaiverForm?.waiver,
            batch: (dataResponse as any)?.code,
          } as CreateTransactionWaiverModel;
          const updatedWaiverForm = {
            ...selectedWaiverForm,
            waiver: updatedWaiver,
          } as CreateTransactionWaiverModelExtension;
          await newWaiver(updatedWaiverForm);
        } catch (waiverErr) {
          console.error("Failed to complete waiver booking link", waiverErr);
          showWarning("Booking created, but signature/waiver failed to save. You can re-sign in the booking details.");
        }
      } catch (err) {
        console.error("Failed to complete booking", err);
        showError("Failed to complete booking. Please try again.");
      } finally {
        setIsCreating(false);
      }
    },
    [
      selectedWaiverForm,
      mapCreateTransaction,
      serviceFormData,
      addonFormData,
      addTransaction,
      newWaiver,
      showWarning,
      showError,
      previousStepper,
      selectedParent,
      pendingParentForm,
      isCreateNewChild,
      createParentAndGuardianAndGetCode,
      createChildrenByParentCode,
      getActiveMemberEndDateTime,
    ]
  );

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  return {
    isCreating,
    componentRef,
    isParentAndChild,
    isCreateNewParent,
    isCreateNewMember,
    addNewChild,
    parentInfo,
    memberInfo,
    pendingParentForm,
    setIsCreating,
    setParentInfo,
    setMemberInfo,
    setIsCreateNewParent,
    setIsCreateNewMember,
    setAddNewChild,
    setPendingParentForm,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
    createChildren,
    createChildrenByParentCode,
    createChildrenByParentCodeAndForms,
    handlePrint,
    createTransactionFromServices,
    createParent,
    createParentAndGetCode,
    createParentAndGetCodeFromForm,
    createParentAndGuardianAndGetCode,
    createParentAndGuardianFromPending,
    createMember,
  };
}
